import { memo, type ReactNode, useCallback, useMemo } from 'react';

import { Calendar, Drawer, Flex } from 'antd';
import cn from 'classnames';
import dayjs from 'dayjs';

import { Loader } from '@/shared/ui/loader';

import type { CalendarData } from '../../models/types/transformed-calendar-data';
import { TradingPlatformDailyTradesCardList } from '../trading-platform-daily-trades-card-list/trading-platform-daily-trades-card-list';

import cls from './trading-platform-calendar-as-drawer.module.css';

interface CustomCalendarCellProps {
    date: dayjs.Dayjs;
    isWin?: boolean;
}

const CalendarCell = memo((props: CustomCalendarCellProps) => {
    const { date, isWin } = props;

    const isToday = date.startOf('day').isSame(dayjs().startOf('day'));

    return (
        <div
            className={cn(
                'algotech-picker-cell-inner algotech-picker-calendar-date',
                {
                    'algotech-picker-calendar-date-today': isToday,
                    ...(isWin !== undefined
                        ? {
                              [cls.win]: isWin,
                              [cls.loss]: !isWin,
                          }
                        : {}),
                },
            )}
        >
            <div className="algotech-picker-calendar-date-value">
                {date.format('DD')}
            </div>
            <div className="algotech-picker-calendar-date-content" />
        </div>
    );
});

CalendarCell.displayName = 'CalendarCell';

interface TradingPlatformCalendarAsDrawerProps {
    isOpened: boolean;
    onClose: VoidFunction;
    titleSlot: ReactNode;
    extraSlot: ReactNode;
    value: dayjs.Dayjs;
    dataSource: Map<DateString, CalendarData>;
    isLoading: boolean;
}

export const TradingPlatformCalendarAsDrawer = memo(
    (props: TradingPlatformCalendarAsDrawerProps) => {
        const {
            value,
            dataSource,
            isOpened,
            onClose,
            titleSlot,
            extraSlot,
            isLoading,
        } = props;

        const validRange = useMemo<[dayjs.Dayjs, dayjs.Dayjs]>(
            () => [value.startOf('month'), value.endOf('month')],
            [value],
        );

        const cellRenderCb = useCallback(
            (date: dayjs.Dayjs) => {
                const data = dataSource.get(date.format('YYYY-MM-DD'));

                return data ? (
                    <CalendarCell
                        date={date}
                        isWin={data.total_profit >= 0}
                    />
                ) : (
                    <CalendarCell date={date} />
                );
            },
            [dataSource],
        );

        return (
            <Drawer
                closeIcon={null}
                extra={extraSlot}
                height="80vh"
                onClose={onClose}
                open={isOpened}
                placement="bottom"
                rootClassName={cls.root}
                title={titleSlot}
            >
                {isLoading ? (
                    <Flex
                        align="center"
                        className={cls.spinContainer}
                        justify="center"
                    >
                        <Loader />
                    </Flex>
                ) : (
                    <Calendar
                        fullCellRender={cellRenderCb}
                        fullscreen={false}
                        headerRender={() => null}
                        validRange={validRange}
                        value={value}
                    />
                )}

                <div className={cls.tradesList}>
                    <TradingPlatformDailyTradesCardList
                        dataSource={dataSource}
                        isLoading={isLoading}
                    />
                </div>
            </Drawer>
        );
    },
);

TradingPlatformCalendarAsDrawer.displayName = 'TradingPlatformCalendarAsDrawer';
